import type { IModelConnection } from "@itwin/core-frontend";
import { ColorDef, GeometryStreamIterator, QueryBinder, QueryRowFormat, readElementMeshes } from "@itwin/core-common";
import { IndexedPolyfaceVisitor } from "@itwin/core-geometry";
import type { MeshExtractOpts, TriMesh } from "../../domain/utilities/Tmesh";
import { shouldUseGeometryStreamHints } from "./colorHintPolicy";

type XYZ = { x: number; y: number; z: number };
type MeshColor = NonNullable<TriMesh["color"]>;
type Range3 = { min: XYZ; max: XYZ };
type GeometryColorHint = { color: MeshColor; rangeWorld: Range3 };
type SubCategoryAppearanceHint = { color?: MeshColor; materialId?: string };

type GeomInfoRow = {
  OriginX?: number;
  OriginY?: number;
  OriginZ?: number;
};

type CategoryRow = {
  CategoryId?: string;
};

const ECSQL_ORIGIN = `
  SELECT g.Origin.X AS OriginX, g.Origin.Y AS OriginY, g.Origin.Z AS OriginZ
  FROM bis.GeometricElement3d g
  WHERE g.ECInstanceId = ?
`;

const ECSQL_ELEMENT_CATEGORY = `
  SELECT g.Category.Id AS CategoryId
  FROM bis.GeometricElement3d g
  WHERE g.ECInstanceId = ?
`;

function makeEmptyRange3(): Range3 {
  return {
    min: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY, z: Number.POSITIVE_INFINITY },
    max: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY, z: Number.NEGATIVE_INFINITY },
  };
}

function expandRange3(range: Range3, p: XYZ): void {
  range.min.x = Math.min(range.min.x, p.x);
  range.min.y = Math.min(range.min.y, p.y);
  range.min.z = Math.min(range.min.z, p.z);
  range.max.x = Math.max(range.max.x, p.x);
  range.max.y = Math.max(range.max.y, p.y);
  range.max.z = Math.max(range.max.z, p.z);
}

function isValidRange3(range: Range3 | undefined): range is Range3 {
  if (!range) return false;
  return (
    Number.isFinite(range.min.x) &&
    Number.isFinite(range.min.y) &&
    Number.isFinite(range.min.z) &&
    Number.isFinite(range.max.x) &&
    Number.isFinite(range.max.y) &&
    Number.isFinite(range.max.z) &&
    range.min.x <= range.max.x &&
    range.min.y <= range.max.y &&
    range.min.z <= range.max.z
  );
}

function axisOverlapScore(aMin: number, aMax: number, bMin: number, bMax: number): number {
  // Axis IoU-like overlap: penalizes tiny ranges fully contained in large ranges.
  const tol = 0.005;
  const a0 = aMin - tol;
  const a1 = aMax + tol;
  const b0 = bMin - tol;
  const b1 = bMax + tol;

  const overlap = Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
  const union = Math.max(a1, b1) - Math.min(a0, b0);
  const score = union <= 1e-9 ? 1 : overlap / union;
  return Math.max(0, Math.min(1, score));
}

function rangeMatchScore(a: Range3, b: Range3): number {
  const sx = axisOverlapScore(a.min.x, a.max.x, b.min.x, b.max.x);
  const sy = axisOverlapScore(a.min.y, a.max.y, b.min.y, b.max.y);
  const sz = axisOverlapScore(a.min.z, a.max.z, b.min.z, b.max.z);
  return (sx + sy + sz) / 3;
}

function rangeSizeSimilarity(a: Range3, b: Range3): number {
  const ax = Math.max(1e-6, a.max.x - a.min.x);
  const ay = Math.max(1e-6, a.max.y - a.min.y);
  const az = Math.max(1e-6, a.max.z - a.min.z);
  const bx = Math.max(1e-6, b.max.x - b.min.x);
  const by = Math.max(1e-6, b.max.y - b.min.y);
  const bz = Math.max(1e-6, b.max.z - b.min.z);

  const va = ax * ay * az;
  const vb = bx * by * bz;
  const minV = Math.min(va, vb);
  const maxV = Math.max(va, vb);
  if (!Number.isFinite(minV) || !Number.isFinite(maxV) || maxV <= 1e-12) return 0;
  return minV / maxV;
}

function transformPointWithAny(localToWorld: any, p: XYZ): XYZ {
  if (localToWorld && typeof localToWorld.multiplyPoint3d === "function") {
    const out = localToWorld.multiplyPoint3d({ x: p.x, y: p.y, z: p.z });
    if (out && typeof out.x === "number" && typeof out.y === "number" && typeof out.z === "number") {
      return { x: out.x, y: out.y, z: out.z };
    }
  }
  return p;
}

function rangeFromLowHigh(low: any, high: any, localToWorld?: any): Range3 | undefined {
  if (!low || !high) return undefined;
  if (
    typeof low.x !== "number" || typeof low.y !== "number" || typeof low.z !== "number" ||
    typeof high.x !== "number" || typeof high.y !== "number" || typeof high.z !== "number"
  ) {
    return undefined;
  }

  const corners: XYZ[] = [
    { x: low.x, y: low.y, z: low.z },
    { x: low.x, y: low.y, z: high.z },
    { x: low.x, y: high.y, z: low.z },
    { x: low.x, y: high.y, z: high.z },
    { x: high.x, y: low.y, z: low.z },
    { x: high.x, y: low.y, z: high.z },
    { x: high.x, y: high.y, z: low.z },
    { x: high.x, y: high.y, z: high.z },
  ];

  const out = makeEmptyRange3();
  for (const c of corners) {
    expandRange3(out, transformPointWithAny(localToWorld, c));
  }
  return isValidRange3(out) ? out : undefined;
}

function tryRangeFromGeometryQuery(geometry: any): Range3 | undefined {
  if (!geometry || typeof geometry !== "object") return undefined;
  try {
    if (typeof geometry.range === "function") {
      const r = geometry.range();
      const out = rangeFromLowHigh(r?.low ?? r?.min, r?.high ?? r?.max);
      if (out) return out;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function tryRangeFromIteratorEntry(entry: any): Range3 | undefined {
  const localRange = entry?.localRange;
  const localToWorld = entry?.localToWorld;

  const fromLocalRange = rangeFromLowHigh(
    localRange?.low ?? localRange?.min,
    localRange?.high ?? localRange?.max,
    localToWorld,
  );
  if (fromLocalRange) return fromLocalRange;

  const primitive = entry?.primitive;
  if (primitive?.type === "geometryQuery") {
    const fromGeometry = tryRangeFromGeometryQuery(primitive.geometry);
    if (fromGeometry) return fromGeometry;
  }

  return undefined;
}

function isCurveLikeGeometryQuery(geometry: any): boolean {
  if (!geometry || typeof geometry !== "object") return false;
  if (typeof geometry.curvePrimitiveType === "string") return true;
  if (typeof geometry.curveCollectionType === "string") return true;
  return false;
}

function pickGeometryHintForRange(
  rangeWorld: Range3 | undefined,
  hints: GeometryColorHint[],
): { color?: MeshColor; score: number } {
  if (!isValidRange3(rangeWorld) || hints.length === 0) return { score: 0 };

  let best: MeshColor | undefined;
  let bestScore = 0;
  let secondBestScore = 0;
  for (const hint of hints) {
    const overlap = rangeMatchScore(rangeWorld, hint.rangeWorld);
    const size = rangeSizeSimilarity(rangeWorld, hint.rangeWorld);
    const score = overlap * (0.10 + 0.90 * size);
    if (score > bestScore) {
      secondBestScore = bestScore;
      bestScore = score;
      best = hint.color;
    } else if (score > secondBestScore) {
      secondBestScore = score;
    }
  }

  // Require a strong enough match and no near-tie ambiguity.
  if (!best || bestScore < 0.42) return { score: bestScore };
  if (bestScore - secondBestScore < 0.10) return { score: bestScore };
  return { color: best, score: bestScore };
}

function getPointCount(pf: any): number {
  const pts = pf?.data?.point;
  if (!pts) return 0;
  if (typeof pf?.data?.pointCount === "number") return pf.data.pointCount;
  if (typeof pts.length === "number") return pts.length;
  if (typeof pts.length === "function") return pts.length();
  return 0;
}

function tryGetPointXYZFromPolyface(pf: any, i: number): XYZ | undefined {
  const pts = pf?.data?.point;
  if (!pts) return undefined;

  if (typeof pts.getPoint3dAt === "function") {
    const p = pts.getPoint3dAt(i);
    if (p && typeof p.x === "number") return { x: p.x, y: p.y, z: p.z };
  }

  if (typeof pts.getPoint3dAtUncheckedPointIndex === "function") {
    const p = pts.getPoint3dAtUncheckedPointIndex(i);
    if (p && typeof p.x === "number") return { x: p.x, y: p.y, z: p.z };
  }

  if (typeof pts.getXAt === "function" && typeof pts.getYAt === "function" && typeof pts.getZAt === "function") {
    return { x: pts.getXAt(i), y: pts.getYAt(i), z: pts.getZAt(i) };
  }

  if (Array.isArray(pts)) {
    const p = pts[i];
    if (!p) return undefined;
    if (typeof p.x === "number") return { x: p.x, y: p.y, z: p.z };
    if (Array.isArray(p) && p.length >= 3) return { x: p[0], y: p[1], z: p[2] };
  }

  return undefined;
}

function triangulateFacetFan(n: number): Array<[number, number, number]> {
  const tris: Array<[number, number, number]> = [];
  if (n < 3) return tris;
  for (let i = 1; i + 1 < n; i++) tris.push([0, i, i + 1]);
  return tris;
}

// Detect 0 vs 1 based on observed indices and pointCount.
function detectClientIndexBase(minIdx: number, maxIdx: number, pointCount: number): 0 | 1 {
  if (minIdx >= 0 && maxIdx < pointCount) return 0;
  if (minIdx >= 1 && maxIdx <= pointCount) return 1;
  return 0;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function colorFromTbgr(tbgr: number): MeshColor {
  const c = ColorDef.fromTbgr(tbgr).colors;
  const alpha = 1 - c.t / 255;
  const color: MeshColor = {
    r: clamp01(c.r / 255),
    g: clamp01(c.g / 255),
    b: clamp01(c.b / 255),
  };
  if (alpha < 1) color.a = clamp01(alpha);
  return color;
}

function colorFromAny(value: unknown): MeshColor | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return colorFromTbgr(value);
  }

  const v = value as any;
  if (!v || typeof v !== "object") return undefined;

  if (typeof v.tbgr === "number") {
    return colorFromTbgr(v.tbgr);
  }

  if (v.colors && typeof v.colors.r === "number") {
    const alpha = 1 - (v.colors.t ?? 0) / 255;
    const color: MeshColor = {
      r: clamp01(v.colors.r / 255),
      g: clamp01(v.colors.g / 255),
      b: clamp01(v.colors.b / 255),
    };
    if (alpha < 1) color.a = clamp01(alpha);
    return color;
  }

  if (typeof v.r === "number" && typeof v.g === "number" && typeof v.b === "number") {
    const scale =
      v.r > 1 || v.g > 1 || v.b > 1
        ? 1 / 255
        : 1;
    const color: MeshColor = {
      r: clamp01(v.r * scale),
      g: clamp01(v.g * scale),
      b: clamp01(v.b * scale),
    };
    if (typeof v.a === "number") color.a = clamp01(v.a);
    return color;
  }

  return undefined;
}

function getArrayLikeLength(arr: any): number {
  if (!arr) return 0;
  if (typeof arr.length === "number") return arr.length;
  if (typeof arr.length === "function") return arr.length();
  return 0;
}

function getFirstIndex(value: any): number | undefined {
  if (!value) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (Array.isArray(value) && value.length) return value[0];
  if (typeof value.length === "number" && value.length > 0) return value[0];
  if (typeof value.length === "function" && value.length() > 0 && typeof value.getAt === "function") {
    return value.getAt(0);
  }
  if (typeof value.getAt === "function") return value.getAt(0);
  if (typeof value.get === "function") return value.get(0);
  return undefined;
}

function getColorAt(colors: any, index: number): unknown {
  if (!colors) return undefined;
  const len = getArrayLikeLength(colors);
  let idx = index;
  if (len > 0) {
    if (idx >= len && idx - 1 >= 0 && idx - 1 < len) idx = idx - 1;
    if (idx < 0) idx = 0;
  }
  if (Array.isArray(colors)) return colors[idx] ?? colors[0];
  if (typeof colors.getAt === "function") return colors.getAt(idx);
  if (typeof colors.get === "function") return colors.get(idx);
  if (typeof colors.getColor === "function") return colors.getColor(idx);
  if (typeof colors.getColorAt === "function") return colors.getColorAt(idx);
  if (typeof colors.getColorDef === "function") return colors.getColorDef(idx);
  return undefined;
}

function decodeColorAtIndex(colors: any, rawIndex: unknown): MeshColor | undefined {
  if (typeof rawIndex !== "number" || !Number.isFinite(rawIndex)) return undefined;
  return colorFromAny(getColorAt(colors, Math.abs(rawIndex)));
}

function colorKey(c: MeshColor): string {
  const a = c.a !== undefined ? c.a : -1;
  return `${c.r.toFixed(6)}|${c.g.toFixed(6)}|${c.b.toFixed(6)}|${a.toFixed(6)}`;
}

function extractMeshColor(pf: any, preferredIndices?: number[]): MeshColor | undefined {
  const data = pf?.data;
  const colors = data?.color;
  if (!colors) return undefined;

  // 1) Prefer the dominant decoded color from facet visitor indices.
  if (Array.isArray(preferredIndices) && preferredIndices.length > 0) {
    const counts = new Map<string, { color: MeshColor; n: number }>();
    for (const idx of preferredIndices) {
      const decoded = decodeColorAtIndex(colors, idx);
      if (!decoded) continue;
      const key = colorKey(decoded);
      const prev = counts.get(key);
      if (prev) {
        prev.n += 1;
      } else {
        counts.set(key, { color: decoded, n: 1 });
      }
    }
    if (counts.size > 0) {
      let best: { color: MeshColor; n: number } | undefined;
      for (const entry of counts.values()) {
        if (!best || entry.n > best.n) best = entry;
      }
      if (best) return best.color;
    }
  }

  // 2) Fall back to the first entry in colorIndex table.
  const rawIndex = getFirstIndex(data?.colorIndex);
  const decodedFromFirstIndex = decodeColorAtIndex(colors, rawIndex);
  if (decodedFromFirstIndex) return decodedFromFirstIndex;

  // 3) Last resort: first color table entry.
  return colorFromAny(getColorAt(colors, 0));
}

function getIndexAt(indices: any, i: number): number | undefined {
  if (!indices) return undefined;
  if (Array.isArray(indices)) return indices[i];
  if (typeof indices.getAt === "function") return indices.getAt(i);
  if (typeof indices.get === "function") return indices.get(i);
  return indices[i];
}

function formatColor(c: MeshColor): string {
  const a = c.a !== undefined ? `, a=${c.a.toFixed(4)}` : "";
  return `r=${c.r.toFixed(4)}, g=${c.g.toFixed(4)}, b=${c.b.toFixed(4)}${a}`;
}

function colorFromColorDef(colorDef: ColorDef, transparency: number | undefined): MeshColor {
  const c = colorDef.colors;
  const t = transparency ?? 0;
  const alpha = 1 - clamp01(t);

  const out: MeshColor = {
    r: clamp01(c.r / 255),
    g: clamp01(c.g / 255),
    b: clamp01(c.b / 255),
  };
  if (alpha < 1) out.a = clamp01(alpha);
  return out;
}

function normalizeId64Like(id: string | undefined): string {
  return (id ?? "").trim().toLowerCase();
}

function id64LikeEqual(a: string | undefined, b: string | undefined): boolean {
  const na = normalizeId64Like(a);
  const nb = normalizeId64Like(b);
  if (!na || !nb) return false;
  if (na === nb) return true;

  // Handle equivalent hex forms (e.g. 0x0017 vs 0x17).
  if (na.startsWith("0x") && nb.startsWith("0x")) {
    try {
      return BigInt(na) === BigInt(nb);
    } catch {
      return false;
    }
  }
  return false;
}

function isValidId64Like(id: string | undefined): boolean {
  const n = normalizeId64Like(id);
  if (!n) return false;
  if (n === "0" || n === "0x0") return false;
  return true;
}

function colorFromRgbFactor(value: unknown): MeshColor | undefined {
  if (!Array.isArray(value) || value.length < 3) return undefined;
  const r = Number(value[0]);
  const g = Number(value[1]);
  const b = Number(value[2]);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return undefined;

  const scale = (r > 1 || g > 1 || b > 1) ? (1 / 255) : 1;
  return {
    r: clamp01(r * scale),
    g: clamp01(g * scale),
    b: clamp01(b * scale),
  };
}

async function queryMaterialColorFromMaterialId(
  iModel: IModelConnection,
  materialId: string | undefined,
  materialColorCache: Map<string, MeshColor | undefined>,
): Promise<MeshColor | undefined> {
  if (!isValidId64Like(materialId)) return undefined;
  const key = normalizeId64Like(materialId);
  if (materialColorCache.has(key)) return materialColorCache.get(key);

  try {
    const props = await iModel.elements.loadProps(materialId as string);
    const p: any = props;
    const renderMaterial =
      p?.jsonProperties?.materialAssets?.renderMaterial ??
      p?.materialAssets?.renderMaterial;

    let color = colorFromRgbFactor(renderMaterial?.color);
    if (color && renderMaterial?.HasTransmit === true && typeof renderMaterial?.transmit === "number") {
      const alpha = 1 - clamp01(renderMaterial.transmit);
      if (alpha < 1) color = { ...color, a: alpha };
    }

    materialColorCache.set(key, color);
    return color;
  } catch {
    materialColorCache.set(key, undefined);
    return undefined;
  }
}

async function querySubCategoryAppearanceHint(
  iModel: IModelConnection,
  categoryId: string | undefined,
  subCategoryId: string | undefined,
  subCategoryAppearanceCache: Map<string, SubCategoryAppearanceHint | undefined>,
): Promise<SubCategoryAppearanceHint | undefined> {
  if (!isValidId64Like(categoryId) || !isValidId64Like(subCategoryId)) return undefined;

  const cacheKey = `${normalizeId64Like(categoryId)}|${normalizeId64Like(subCategoryId)}`;
  if (subCategoryAppearanceCache.has(cacheKey)) return subCategoryAppearanceCache.get(cacheKey);

  try {
    let hint: SubCategoryAppearanceHint | undefined;

    if (id64LikeEqual(subCategoryId, categoryId)) {
      const categoryInfoMap = await iModel.categories.getCategoryInfo([categoryId as string]);
      const categoryInfo = categoryInfoMap.get(categoryId as string);
      if (categoryInfo) {
        for (const [scId, subCategory] of categoryInfo.subCategories.entries()) {
          if (!id64LikeEqual(scId, subCategoryId)) continue;
          if (subCategory.appearance.invisible) break;
          hint = {
            color: colorFromColorDef(subCategory.appearance.color, subCategory.appearance.transparency),
            materialId: subCategory.appearance.materialId,
          };
          break;
        }
      }
    } else {
      const subInfoMap = await iModel.categories.getSubCategoryInfo({
        category: categoryId as string,
        subCategories: [subCategoryId as string],
      });
      const subInfo = subInfoMap.get(subCategoryId as string);
      if (subInfo && !subInfo.appearance.invisible) {
        hint = {
          color: colorFromColorDef(subInfo.appearance.color, subInfo.appearance.transparency),
          materialId: subInfo.appearance.materialId,
        };
      }
    }

    subCategoryAppearanceCache.set(cacheKey, hint);
    return hint;
  } catch {
    subCategoryAppearanceCache.set(cacheKey, undefined);
    return undefined;
  }
}

async function queryFallbackColorFromCategoryAppearance(
  iModel: IModelConnection,
  categoryId: string | undefined,
): Promise<MeshColor | undefined> {
  if (!categoryId) return undefined;

  const categoryInfoMap = await iModel.categories.getCategoryInfo([categoryId]);
  const categoryInfo = categoryInfoMap.get(categoryId);
  if (!categoryInfo) return undefined;

  // Prefer the default subcategory (same Id as category) only.
  for (const [subCategoryId, subCategory] of categoryInfo.subCategories.entries()) {
    if (!id64LikeEqual(subCategoryId, categoryId)) continue;
    if (subCategory.appearance.invisible) return undefined;
    return colorFromColorDef(subCategory.appearance.color, subCategory.appearance.transparency);
  }

  // Do not pick an arbitrary visible subcategory; that can apply unrelated colors.
  return undefined;
}

async function queryElementCategoryId(
  iModel: IModelConnection,
  elementId: string,
): Promise<string | undefined> {
  const reader = iModel.createQueryReader(
    ECSQL_ELEMENT_CATEGORY,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseECSqlPropertyNames },
  );

  for await (const row of reader) {
    const r = (row as any).toRow?.() as CategoryRow;
    return r?.CategoryId;
  }
  return undefined;
}

function makeIteratorForElement(props: any): GeometryStreamIterator | undefined {
  if (!props?.geom) return undefined;

  try {
    return GeometryStreamIterator.fromGeometricElement3d(props);
  } catch {
    // ignore and try 2d
  }

  try {
    return GeometryStreamIterator.fromGeometricElement2d(props);
  } catch {
    // ignore and fallback
  }

  try {
    return new GeometryStreamIterator(props.geom, props.category);
  } catch {
    return undefined;
  }
}

async function colorFromGeomParams(
  iModel: IModelConnection,
  geomParams: any,
  categoryId: string | undefined,
  subCategoryAppearanceCache: Map<string, SubCategoryAppearanceHint | undefined>,
  materialColorCache: Map<string, MeshColor | undefined>,
  allowLineColorFallback = false,
): Promise<MeshColor | undefined> {
  let subCategoryAppearance: SubCategoryAppearanceHint | undefined;
  const subCategoryId = typeof geomParams?.subCategoryId === "string" ? geomParams.subCategoryId : undefined;
  if (categoryId && subCategoryId) {
    subCategoryAppearance = await querySubCategoryAppearanceHint(
      iModel,
      categoryId,
      subCategoryId,
      subCategoryAppearanceCache,
    );
  }

  const materialIdFromGeomParams =
    typeof geomParams?.materialId === "string" ? geomParams.materialId : undefined;
  const materialId = isValidId64Like(materialIdFromGeomParams)
    ? materialIdFromGeomParams
    : subCategoryAppearance?.materialId;

  const materialColor = await queryMaterialColorFromMaterialId(
    iModel,
    materialId,
    materialColorCache,
  );
  if (materialColor) return materialColor;

  if (subCategoryAppearance?.color) {
    return subCategoryAppearance.color;
  }

  if (categoryId) {
    const defaultCategoryColor = await queryFallbackColorFromCategoryAppearance(iModel, categoryId);
    if (defaultCategoryColor) return defaultCategoryColor;
  }

  const fillColor = geomParams?.fillColor;
  if (fillColor && typeof fillColor === "object") {
    try {
      const transparency =
        typeof geomParams?.fillTransparency === "number"
          ? geomParams.fillTransparency
          : typeof geomParams?.elmTransparency === "number"
            ? geomParams.elmTransparency
            : undefined;
      return colorFromColorDef(fillColor as ColorDef, transparency);
    } catch {
      // ignore
    }
  }

  if (allowLineColorFallback) {
    const lineColor = geomParams?.lineColor;
    if (lineColor && typeof lineColor === "object") {
      try {
        const transparency =
          typeof geomParams?.elmTransparency === "number"
            ? geomParams.elmTransparency
            : undefined;
        return colorFromColorDef(lineColor as ColorDef, transparency);
      } catch {
        // ignore
      }
    }
  }

  return undefined;
}

async function collectGeometryStreamColorHints(
  iModel: IModelConnection,
  elementId: string,
  categoryId: string | undefined,
): Promise<GeometryColorHint[]> {
  const props = (await iModel.elements.loadProps(elementId, { wantGeometry: true })) as any;
  if (!props?.geom) return [];

  const rootIter = makeIteratorForElement(props);
  if (!rootIter) return [];

  const hints: GeometryColorHint[] = [];
  const subCategoryAppearanceCache = new Map<string, SubCategoryAppearanceHint | undefined>();
  const materialColorCache = new Map<string, MeshColor | undefined>();
  const partCache = new Map<string, any>();

  const walk = async (iter: GeometryStreamIterator): Promise<void> => {
    for (const entry of iter) {
      const primitive = (entry as any)?.primitive;
      const primitiveType = primitive?.type as string | undefined;

      if (primitiveType === "partReference" && primitive?.part?.id) {
        const partId = primitive.part.id as string;
        let partProps = partCache.get(partId);
        if (!partProps) {
          partProps = await iModel.elements.loadProps(partId, { wantGeometry: true });
          if (partProps) partCache.set(partId, partProps);
        }
        if (!partProps?.geom) continue;

        let partIter: GeometryStreamIterator | undefined;
        try {
          partIter = GeometryStreamIterator.fromGeometryPart(
            partProps,
            (entry as any).geomParams,
            (iter as any).partToWorld?.(),
          );
        } catch {
          partIter = undefined;
        }
        if (partIter) await walk(partIter);
        continue;
      }

      if (primitiveType !== "geometryQuery" && primitiveType !== "brep") continue;
      if (primitiveType === "geometryQuery" && isCurveLikeGeometryQuery(primitive?.geometry)) continue;
      const c = await colorFromGeomParams(
        iModel,
        (entry as any).geomParams,
        categoryId,
        subCategoryAppearanceCache,
        materialColorCache,
      );
      if (!c) continue;

      const rangeWorld = tryRangeFromIteratorEntry(entry as any);
      if (!rangeWorld) continue;

      hints.push({ color: c, rangeWorld });
      if (hints.length > 512) return;
    }
  };

  await walk(rootIter);
  return hints;
}

export async function extractTriMeshesForElement(
  iModel: IModelConnection,
  elementId: string,
  opts?: MeshExtractOpts,
): Promise<TriMesh[]> {

  // console.log("[MeshExtract] start", { elementId });

  const makeLocal = opts?.makeLocal ?? true;
  const unitScale = opts?.unitScale ?? 1.0;
  const maxPolyfaces = opts?.maxPolyfaces ?? Number.POSITIVE_INFINITY;
  const dropDegenerate = opts?.dropDegenerate ?? true;
  const validateRange = opts?.validateRange ?? true;
  const debugColor = opts?.debugColor ?? false;
  const debugColorPreviewN = opts?.debugColorPreviewN ?? 12;
  const useCategoryAppearanceFallback = opts?.useCategoryAppearanceFallback ?? true;
  const useGeometryStreamHints = true;

  // 1) Origin
  let origin: XYZ = { x: 0, y: 0, z: 0 };
  {
    const reader = iModel.createQueryReader(
      ECSQL_ORIGIN,
      QueryBinder.from([elementId]),
      { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
    );

    for await (const row of reader) {
      const r = row.toRow() as any as GeomInfoRow;
      origin = { x: r.OriginX ?? 0, y: r.OriginY ?? 0, z: r.OriginZ ?? 0 };
      break;
    }
  }

  // 2) Decode polyfaces
  const meshBytes = await iModel.generateElementMeshes({ source: elementId });
  const polyfaces = readElementMeshes(meshBytes) as any[];
  // console.log("[MeshExtract] polyfaces decoded", {
  //   elementId,
  //   polyfaceCount: polyfaces.length,
  // });
  if (!polyfaces.length) return [];
  if (debugColor) {
    console.groupCollapsed(`[MeshColorDiag] START element=${elementId}`);
    console.log("[MeshColorDiag] decoded polyfaces:", polyfaces.length);
  }

  let categoryFallbackColor: MeshColor | undefined;
  let elementCategoryId: string | undefined;
  let geometryStreamColorHints: GeometryColorHint[] = [];
  try {
    elementCategoryId = await queryElementCategoryId(iModel, elementId);
  } catch (err) {
    if (debugColor) console.warn("[MeshColorDiag] Failed to query element category id:", err);
  }

  if (useCategoryAppearanceFallback) {
    try {
      categoryFallbackColor = await queryFallbackColorFromCategoryAppearance(iModel, elementCategoryId);
      if (debugColor && categoryFallbackColor) {
        console.log("[MeshColorDiag] category/subcategory appearance fallback color:", formatColor(categoryFallbackColor));
      }
    } catch (err) {
      if (debugColor) {
        console.warn("[MeshColorDiag] Failed to resolve category appearance fallback color:", err);
      }
    }
  }
  if (useGeometryStreamHints) {
    try {
      geometryStreamColorHints = await collectGeometryStreamColorHints(iModel, elementId, elementCategoryId);
      if (debugColor) {
        const hintColors = geometryStreamColorHints.map((h) => formatColor(h.color));
        console.log("[MeshColorDiag] geometry stream color hints:", {
          count: geometryStreamColorHints.length,
          preview: hintColors.slice(0, debugColorPreviewN),
        });
      }
    } catch (err) {
      if (debugColor) console.warn("[MeshColorDiag] Failed to collect geometry stream color hints:", err);
    }
  }
  const geometryStreamHintColorCount = new Set(
    geometryStreamColorHints.map((h) => colorKey(h.color))
  ).size;
  // Single-color elements (common for conduits) can still rely on geometry-stream hints.
  // Keep range matching as the guardrail instead of requiring multiple distinct colors.
  const geometryStreamHintsUsable = shouldUseGeometryStreamHints(
    useGeometryStreamHints,
    geometryStreamColorHints.length
  );
  if (debugColor && useGeometryStreamHints && !geometryStreamHintsUsable) {
    console.log("[MeshColorDiag] Geometry stream hints disabled for this element (no usable hints collected).", {
      hintCount: geometryStreamColorHints.length,
      distinctHintColors: geometryStreamHintColorCount,
    });
  }

  const out: TriMesh[] = [];
  const pfLimit = Math.min(polyfaces.length, maxPolyfaces);

  for (let pfIndex = 0; pfIndex < pfLimit; pfIndex++) {
    const pf = polyfaces[pfIndex];
    const pointCount = getPointCount(pf);
    if (pointCount <= 0) continue;
    const data = pf?.data;
    const polyfaceRangeWorld = makeEmptyRange3();

    // Points
    const points: Array<[number, number, number]> = new Array(pointCount);
    for (let i = 0; i < pointCount; i++) {
      const p = tryGetPointXYZFromPolyface(pf, i);
      if (!p) {
        points[i] = [0, 0, 0];
        continue;
      }
      const x = (makeLocal ? (p.x - origin.x) : p.x) * unitScale;
      const y = (makeLocal ? (p.y - origin.y) : p.y) * unitScale;
      const z = (makeLocal ? (p.z - origin.z) : p.z) * unitScale;
      expandRange3(polyfaceRangeWorld, p);
      points[i] = [x, y, z];
    }

    // Triangles (visitor)
    const rawTris: Array<{ a: number; b: number; c: number; colorIdx?: number }> = [];
    let minIdx = Number.POSITIVE_INFINITY;
    let maxIdx = Number.NEGATIVE_INFINITY;

    const visitor = IndexedPolyfaceVisitor.create(pf as any, 0);
    visitor.reset();
    const sampleClientColorIdx: number[] = [];
    const candidateColorIndices: number[] = [];

    while (visitor.moveToNextFacet()) {
      const pts = visitor.point as any;
      const nFacetPts =
        typeof pts.length === "number" ? pts.length :
        typeof pts.length === "function" ? pts.length() :
        0;

      if (nFacetPts < 3) continue;

      // Collect absolute client indices for this facet
      const clientIdx: number[] = new Array(nFacetPts);
      let facetColorIdx: number | undefined;
      for (let i = 0; i < nFacetPts; i++) {
        const ciAbs = Math.abs(visitor.clientPointIndex(i));
        clientIdx[i] = ciAbs;
        minIdx = Math.min(minIdx, ciAbs);
        maxIdx = Math.max(maxIdx, ciAbs);

        const colorIdx = Math.abs(visitor.clientColorIndex(i));
        if (Number.isFinite(colorIdx)) {
          if (facetColorIdx === undefined) facetColorIdx = colorIdx;
          if (candidateColorIndices.length < 64) candidateColorIndices.push(colorIdx);
          if (debugColor && sampleClientColorIdx.length < debugColorPreviewN) {
            sampleClientColorIdx.push(colorIdx);
          }
        }
      }

      // Fan triangulate facet-local, map to client indices
      for (const [aL, bL, cL] of triangulateFacetFan(nFacetPts)) {
        rawTris.push({
          a: clientIdx[aL],
          b: clientIdx[bL],
          c: clientIdx[cL],
          colorIdx: facetColorIdx,
        });
      }
    }

    if (!rawTris.length) continue;

    const base = detectClientIndexBase(minIdx, maxIdx, pointCount);

    // Normalize to 0-based indices into points[]
    const normalizedTris: Array<{ tri: [number, number, number]; colorIdx?: number }> = [];
    for (const t of rawTris) {
      const a = t.a;
      const b = t.b;
      const c = t.c;
      const ia = a - base;
      const ib = b - base;
      const ic = c - base;

      if (dropDegenerate && (ia === ib || ib === ic || ia === ic)) continue;

      if (validateRange) {
        if (ia < 0 || ib < 0 || ic < 0) continue;
        if (ia >= pointCount || ib >= pointCount || ic >= pointCount) continue;
      }

      normalizedTris.push({ tri: [ia, ib, ic], colorIdx: t.colorIdx });
    }

    if (!normalizedTris.length) continue;
    const payloadColor = extractMeshColor(pf, candidateColorIndices);
    // Color precedence contract:
    // 1) explicit polyface payload color (best fidelity)
    // 2) geometry-stream range-matched hint (fallback when mesh payload omitted)
    // 3) category/subcategory appearance fallback (last resort)
    const streamHint = payloadColor || !geometryStreamHintsUsable
      ? { color: undefined, score: 0 }
      : pickGeometryHintForRange(polyfaceRangeWorld, geometryStreamColorHints);
    const streamHintColor = streamHint.color;
    const color = payloadColor ?? streamHintColor ?? categoryFallbackColor;

    // If per-triangle color indices decode to multiple colors, split the polyface into
    // multiple TriMesh outputs (same points, subset triangles per color).
    // This preserves downstream IFC styling by face set instead of collapsing to one color.
    const colorPayload = data?.color;
    const trianglesNoColor: Array<[number, number, number]> = [];
    const trianglesByColor = new Map<string, { color: MeshColor; triangles: Array<[number, number, number]> }>();
    for (const { tri, colorIdx } of normalizedTris) {
      const decoded = decodeColorAtIndex(colorPayload, colorIdx);
      if (!decoded) {
        trianglesNoColor.push(tri);
        continue;
      }
      const key = colorKey(decoded);
      const bucket = trianglesByColor.get(key);
      if (bucket) {
        bucket.triangles.push(tri);
      } else {
        trianglesByColor.set(key, { color: decoded, triangles: [tri] });
      }
    }

    if (trianglesByColor.size > 0) {
      for (const bucket of trianglesByColor.values()) {
        if (!bucket.triangles.length) continue;
        out.push({ points, triangles: bucket.triangles, color: bucket.color });
      }

      if (trianglesNoColor.length) {
        const uncoloredFallback = color ?? undefined;
        out.push(
          uncoloredFallback
            ? { points, triangles: trianglesNoColor, color: uncoloredFallback }
            : { points, triangles: trianglesNoColor }
        );
      }
    } else {
      const triangles = normalizedTris.map((t) => t.tri);
      out.push(color ? { points, triangles, color } : { points, triangles });
    }

    if (debugColor) {
      const colorCount = getArrayLikeLength(data?.color);
      const colorIndexLen = getArrayLikeLength(data?.colorIndex);

      const colorIndexPreview: number[] = [];
      for (let i = 0; i < Math.min(colorIndexLen, debugColorPreviewN); i++) {
        const raw = getIndexAt(data?.colorIndex, i);
        if (typeof raw === "number") colorIndexPreview.push(raw);
      }

      const colorTablePreview: string[] = [];
      const colorDecodedSet = new Set<string>();
      for (let i = 0; i < Math.min(colorCount, debugColorPreviewN); i++) {
        const raw = getColorAt(data?.color, i);
        const decoded = colorFromAny(raw);
        if (!decoded) continue;
        const key = `${decoded.r.toFixed(4)},${decoded.g.toFixed(4)},${decoded.b.toFixed(4)},${decoded.a?.toFixed(4) ?? "-"}`;
        colorDecodedSet.add(key);
        colorTablePreview.push(`#${i}: ${formatColor(decoded)}`);
      }

      const visitorDecodedSet = new Set<string>();
      for (const ci of sampleClientColorIdx) {
        const raw = getColorAt(data?.color, ci);
        const decoded = colorFromAny(raw);
        if (!decoded) continue;
        visitorDecodedSet.add(`${decoded.r.toFixed(4)},${decoded.g.toFixed(4)},${decoded.b.toFixed(4)},${decoded.a?.toFixed(4) ?? "-"}`);
      }

      console.groupCollapsed(`[MeshColorDiag] polyface=${pfIndex}`);
      console.log("[MeshColorDiag] stats:", {
        pointCount,
        triangleCount: normalizedTris.length,
        colorCount,
        colorIndexLen,
        meshColorDecoded: payloadColor ? formatColor(payloadColor) : undefined,
        meshColorFromGeometryStreamHint: streamHintColor ? formatColor(streamHintColor) : undefined,
        meshColorFromGeometryStreamHintScore: streamHint.score,
        meshColorWithFallback: color ? formatColor(color) : undefined,
        splitColorBuckets: trianglesByColor.size,
        splitUncoloredTriangles: trianglesNoColor.length,
      });
      console.log("[MeshColorDiag] colorIndex preview (raw):", colorIndexPreview);
      console.log("[MeshColorDiag] visitor clientColorIndex preview:", sampleClientColorIdx);
      console.log("[MeshColorDiag] color table preview:", colorTablePreview);
      console.log("[MeshColorDiag] unique decoded colors in color table preview:", colorDecodedSet.size);
      console.log("[MeshColorDiag] unique decoded colors from visitor index preview:", visitorDecodedSet.size);
      if (colorCount === 0 && colorIndexLen === 0) {
        console.warn("[MeshColorDiag] No color payload present on polyface (generateElementMeshes may be omitting display symbology/material).");
        if (categoryFallbackColor) {
          console.log("[MeshColorDiag] Applying category/subcategory appearance fallback color for this polyface.");
        }
      }
      console.groupEnd();
    }

    if (!color) {
      const colorCount = getArrayLikeLength(data?.color);
      const colorIndexLen = getArrayLikeLength(data?.colorIndex);
      if (colorCount > 0 || colorIndexLen > 0) {
        // console.log("[MeshColor] Color data present but could not decode.", {
        //   colorCount,
        //   colorIndexLen,
        //   colorIndexSample: getFirstIndex(data?.colorIndex),
        // });
      }
    } else {
      // console.log("[MeshColor] Decoded mesh color:", color);
    }

  }

  if (debugColor) {
    console.log(`[MeshColorDiag] END element=${elementId}`);
    console.groupEnd();
  }

  return out;
}
